import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  AlertColor,
  Autocomplete,
  AutocompleteInputChangeReason,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import * as Sentry from '@sentry/react';

/**
 * Jalon du parcours de soumission DA, en breadcrumb et non en événement.
 *
 * Ces onze jalons partaient chacun en `captureMessage` info : ~10 événements par
 * DA soumise, ~1 100 sur le cycle Sentry de septembre-octobre 2026 (22 % du quota
 * de 5 000 erreurs/mois, dépassé le 6/10 → monitoring aveugle une semaine). En
 * breadcrumb, le fil des étapes reste attaché à tout événement émis ensuite dans
 * la session (erreur, « Duplicate Attempt », « marked invisible ») et au replay,
 * sans consommer de quota. Les deux warnings restent des événements.
 */
const daBreadcrumb = (message: string, action: string, data: Record<string, unknown>): void => {
  Sentry.addBreadcrumb({ category: 'da_submission', level: 'info', message, data: { action, ...data } });
};

import { makeAuthenticatedApiCall } from '../apiHelper';
import { useAuth } from '../components/AuthContext';
import { useDAData, useInvoicingTypeByOrgId, useOrganizationInDb } from '../hooks/useApiQueries';
import { cleanSiret, formatSiret, formatUTCDateTime, removeSpaces, validateSiret } from '../utils';
import { deriveAddressFromStructuredOrg, hasStructuredAddress, parseFreeTextAddress, resolveVoieCode } from '../addressTypes';
import FilesOpener from './CGForm/FilesOpener';
import FirebaseUploadComponent from './CreateCotation/FirebaseUploadComponent';
import { ErrorMessageSnackbar } from './ErrorMessageSnackbar';
import FileCarouselDialog from './FileCarouselDialog';

interface ApiResponse<T> {
  data: T;
}

interface CityData {
  name: string;
  postcode: string;
}

interface RegistrationResponse {
  registrationId: number;
}

interface QontoResponse {
  success?: boolean;
  message?: string;
  invoiceFirebaseUrl?: string;
}

interface CityOption {
  label: string;
  value: string;
}

interface DAData {
  id: number;
  reg_plate: string;
  vin: string;
  num_formule: string;
  purchase_date_time: string;
  status: string;
  org_id?: number;
  person_id?: number;
  created_at: string;
  updated_at: string;
  visible: boolean;
  buyer?: {
    id: number;
    registration_id: number;
    client_type: 'person' | 'pro';
    first_name?: string;
    last_name?: string;
    usual_name?: string;
    gender?: string;
    date_of_birth?: string;
    place_of_birth?: string;
    country_of_birth?: string;
    department_of_birth?: string;
    address_number?: string;
    address_extension?: string;
    address_type?: string;
    address_name?: string;
    address_place?: string;
    address_stairs?: string;
    address_comp?: string;
    address_postal_box?: string;
    city?: string;
    postal_code?: string;
    country?: string;
    phone?: string;
    siret?: string;
    company_name?: string;
    files?: string;
    residency_files?: string;
    document_type?: string;
    document_id?: string;
    document_issue_date?: string;
  };
  seller?: {
    id: number;
    registration_id: number;
    client_type: 'person' | 'pro';
    first_name?: string;
    last_name?: string;
    usual_name?: string;
    gender?: string;
    date_of_birth?: string;
    place_of_birth?: string;
    country_of_birth?: string;
    department_of_birth?: string;
    address_number?: string;
    address_extension?: string;
    address_type?: string;
    address_name?: string;
    address_place?: string;
    address_stairs?: string;
    address_comp?: string;
    address_postal_box?: string;
    city?: string;
    postal_code?: string;
    country?: string;
    siret?: string;
    company_name?: string;
    files?:
      | {
          certificate?: string[];
          cerfa_achat?: string[];
          cerfa_cession?: string[];
          mandat?: string[];
          recepisse?: string[];
        }
      | string; // Support pour l'ancien format string
  };
  misiv?: {
    id: number;
    registration_id: number;
    response_id?: number;
    receipt_file_name?: string;
    receipt_file_firebase_url?: string;
    has_timeout: number;
    lpn_id?: string;
    step: number;
    created_at: string;
    updated_at: string;
  };
  commentFilesUrls?: string;
  internalCommentFilesUrls?: string;
}

type FormValues = {
  regPlate: string;
  vin: string;
  numFormule: string;
  purchaseDateTime: string;
  sellerFilesUrls: string[];
  buyerFilesUrls: string[];
  buyerResidencyFilesUrls: string[];
  coBuyerFilesUrls: string[];
  sellerClientType: string;
  sellerFirstName: string;
  sellerLastName: string;
  sellerSiret: string;
  sellerCompanyName: string;
  buyerSiret: string;
  buyerCompanyName: string;
  buyerAddressNumber: string;
  buyerAddressExtension: string;
  buyerAddressType: string;
  buyerAddressName: string;
  buyerAddressPlace: string;
  buyerAddressStairs: string;
  buyerAddressComp: string;
  buyerAddressPostalBox: string;
  buyerCity: string;
  buyerPostalCode: string;
  buyerCountry: string;
};

const formatRegPlate = (value: string): string => {
  const alphanumericValue = value.replace(/[^A-Z0-9]/gi, '');
  if (alphanumericValue.length > 2 && alphanumericValue.length <= 5) {
    return `${alphanumericValue.slice(0, 2)}-${alphanumericValue.slice(2)}`;
  } else if (alphanumericValue.length > 5) {
    return `${alphanumericValue.slice(0, 2)}-${alphanumericValue.slice(2, 5)}-${alphanumericValue.slice(5)}`;
  }
  return alphanumericValue.substring(0, 9);
};

const DAForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const registrationIdParam = searchParams.get('registrationId');
  const isReadOnly = !!registrationIdParam;

  const { currentUser } = useAuth();
  const orgId = currentUser?.data?.organization?.value;
  const { data: orgInDb } = useOrganizationInDb(orgId ? String(orgId) : '');
  const { data: invoicingType } = useInvoicingTypeByOrgId(orgId ?? 0);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Protection renforcée contre les clics multiples
  const [hasSubmissionError, setHasSubmissionError] = useState(false); // Garde le bouton désactivé après erreur
  const [registrationId, setRegistrationId] = useState<number | null>(
    registrationIdParam ? parseInt(registrationIdParam) : null,
  );
  const [currentStatus, setCurrentStatus] = useState('DA_REQUEST');

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('error');

  const [purchaseDateTime, setPurchaseDateTime] = useState('');

  // Nouveaux états pour le progress et les étapes
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');

  const [birthDepartmentOptions, setBirthDepartmentOptions] = useState<Array<CityOption>>([]);
  const [cityOptions, setCityOptions] = useState<Array<CityOption>>([]);
  const [selectedPostalCode, setSelectedPostalCode] = useState('');
  const [selectedCity, setSelectedCity] = useState('');

  const [sellerFilesUrls, setSellerFilesUrls] = useState<string[]>([]);
  const [buyerFilesUrls, setBuyerFilesUrls] = useState<string[]>([]);
  const [buyerResidencyFilesUrls, setBuyerResidencyFilesUrls] = useState<string[]>([]);
  const [coBuyerFilesUrls, setCoBuyerFilesUrls] = useState<string[]>([]);
  const [certificateUrls, setCertificateUrls] = useState<string[]>([]);
  const [cerfaAchatUrls, setCerfaAchatUrls] = useState<string[]>([]);
  const [cerfaCessionUrls, setCerfaCessionUrls] = useState<string[]>([]);
  const [mandatUrls, setMandatUrls] = useState<string[]>([]);
  const [recepisseUrls, setRecepisseUrls] = useState<string[]>([]);
  const [kbisUrls, setKbisUrls] = useState<string[]>([]); // Nouvel état pour les fichiers KBIS

  const [modalOpen, setModalOpen] = useState(false);
  const [acquirerType, setAcquirerType] = useState<'self' | 'other'>('self'); // Nouvel état pour gérer le type d'acquéreur

  // Carousel state
  const [carouselOpen, setCarouselOpen] = useState<boolean>(false);
  const [carouselIndex, setCarouselIndex] = useState<number>(0);

  const { data: daData, isLoading: isLoadingDAData } = registrationId
    ? (useDAData(registrationId.toString()) as { data: DAData | undefined; isLoading: boolean })
    : { data: null, isLoading: false };

  const formMethods = useForm<FormValues>({
    mode: 'all',
    defaultValues: {
      // Identifiants et informations de base du véhicule
      regPlate: '',
      vin: '',
      numFormule: '',
      purchaseDateTime: '',

      // Informations sur les fichiers
      sellerFilesUrls: [],
      buyerFilesUrls: [],
      buyerResidencyFilesUrls: [],
      coBuyerFilesUrls: [],

      // Détails de l'ancien propriétaire
      sellerClientType: 'person',
      sellerFirstName: '',
      sellerLastName: '',
      sellerSiret: '',
      sellerCompanyName: '',

      // Détails du nouveau propriétaire (toujours professionnel)
      buyerSiret: '',
      buyerCompanyName: '',

      // Adresse du nouveau propriétaire
      buyerAddressNumber: '',
      buyerAddressExtension: '',
      buyerAddressType: '',
      buyerAddressName: '',
      buyerAddressPlace: '',
      buyerAddressStairs: '',
      buyerAddressComp: '',
      buyerAddressPostalBox: '',
      buyerCity: '',
      buyerPostalCode: '',
      buyerCountry: '',
    },
  });

  const { control, formState, reset, trigger, watch, setValue, setError, clearErrors } = formMethods;
  const { errors, isValid } = formState;

  const sellerClientType = watch('sellerClientType');
  const regPlateValue = watch('regPlate');

  const allCarouselFiles = useMemo(() => {
    if (!isReadOnly) return [];
    const urls: string[] = [];
    const addUrls = (source: string[] | undefined | null) => {
      if (!source) return;
      source.forEach((u) => {
        if (u && !urls.includes(u)) urls.push(u);
      });
    };
    const addUrlString = (source: string | undefined | null) => {
      if (!source) return;
      source
        .split(',')
        .map((u) => u.trim())
        .forEach((u) => {
          if (u && !urls.includes(u)) urls.push(u);
        });
    };

    addUrls(certificateUrls);
    addUrls(cerfaAchatUrls);
    addUrls(cerfaCessionUrls);
    addUrls(mandatUrls);
    addUrls(recepisseUrls);
    addUrls(kbisUrls);
    addUrlString(daData?.commentFilesUrls);
    addUrlString(daData?.internalCommentFilesUrls);

    return urls;
  }, [certificateUrls, cerfaAchatUrls, cerfaCessionUrls, mandatUrls, recepisseUrls, kbisUrls, daData, isReadOnly]);

  const handleCarouselOpen = (url: string) => {
    const index = allCarouselFiles.indexOf(url);
    setCarouselIndex(index >= 0 ? index : 0);
    setCarouselOpen(true);
  };

  // Vérification des documents obligatoires
  const requiredDocsValid =
    certificateUrls.length > 0 && cerfaAchatUrls.length > 0 && cerfaCessionUrls.length > 0 && mandatUrls.length > 0;

  // Vérification du KBIS pour "Autre entité"
  const kbisValid = acquirerType === 'self' || (acquirerType === 'other' && kbisUrls.length > 0);

  // Vérification complète pour activer/désactiver le bouton (buyer toujours professionnel)
  const isFormCompletelyValid = isValid && requiredDocsValid && kbisValid;

  useEffect(() => {
    if (daData) {
      reset({
        regPlate: daData.reg_plate || '',
        vin: daData.vin || '',
        numFormule: daData.num_formule || '',
        purchaseDateTime: daData.purchase_date_time ? formatUTCDateTime(daData.purchase_date_time) : '',

        // Seller data
        sellerClientType: daData.seller?.client_type || 'person',
        sellerFirstName: daData.seller?.first_name || '',
        sellerLastName: daData.seller?.last_name || '',
        sellerCompanyName: daData.seller?.company_name || '',
        sellerSiret: daData.seller?.siret || '',

        // Buyer data (professionnel uniquement)
        buyerCompanyName: daData.buyer?.company_name || '',
        buyerSiret: daData.buyer?.siret || '',
        buyerAddressNumber: daData.buyer?.address_number || '',
        buyerAddressExtension: daData.buyer?.address_extension || '',
        // Normalise le code stocké (legacy che/chm, MAJUSCULE, libellé…) vers le
        // code du dropdown pour qu'il s'affiche au lieu de retomber sur « Inconnu ».
        buyerAddressType: daData.buyer?.address_type ? resolveVoieCode(daData.buyer.address_type) : '',
        buyerAddressName: daData.buyer?.address_name || '',
        buyerAddressPlace: daData.buyer?.address_place || '',
        buyerAddressStairs: daData.buyer?.address_stairs || '',
        buyerAddressComp: daData.buyer?.address_comp || '',
        buyerAddressPostalBox: daData.buyer?.address_postal_box || '',
        buyerCity: daData.buyer?.city || '',
        buyerPostalCode: daData.buyer?.postal_code || '',
        buyerCountry: daData.buyer?.country || '',
      });

      // Mise à jour des URLs des fichiers
      if (daData.seller?.files) {
        try {
          // Si files est un objet JSON
          if (typeof daData.seller.files === 'object') {
            const files = daData.seller.files as {
              certificate?: string[];
              cerfa_achat?: string[];
              cerfa_cession?: string[];
              mandat?: string[];
              recepisse?: string[];
            };
            setCertificateUrls(files.certificate || []);
            setCerfaAchatUrls(files.cerfa_achat || []);
            setCerfaCessionUrls(files.cerfa_cession || []);
            setMandatUrls(files.mandat || []);
            setRecepisseUrls(files.recepisse || []);
          } else if (typeof daData.seller.files === 'string') {
            // Compatibilité avec l'ancien format (string séparée par virgules)
            const fileUrls = daData.seller.files.split(',').map((url: string) => url.trim());
            // Mettre tous les fichiers dans certificate par défaut pour la compatibilité
            setCertificateUrls(fileUrls);
          }
        } catch (error) {
          console.error('Error parsing seller files:', error);
          // Fallback vers l'ancien format
          if (typeof daData.seller.files === 'string') {
            setCertificateUrls(daData.seller.files.split(',').map((url: string) => url.trim()));
          }
        }
      }
      if (daData.buyer?.files) {
        setBuyerFilesUrls(daData.buyer.files.split(',').map((url: string) => url.trim()));
        // Si on est en mode lecture seule et qu'il y a des fichiers buyer, c'est probablement du KBIS
        if (isReadOnly) {
          setKbisUrls(daData.buyer.files.split(',').map((url: string) => url.trim()));
        }
      }
      if (daData.buyer?.residency_files) {
        setBuyerResidencyFilesUrls(daData.buyer.residency_files.split(',').map((url: string) => url.trim()));
      }
    }
  }, [daData, reset, isReadOnly]);

  // Mettre à jour les états selectedPostalCode et selectedCity quand daData change
  useEffect(() => {
    if (daData?.buyer) {
      setSelectedPostalCode(daData.buyer.postal_code || '');
      setSelectedCity(daData.buyer.city || '');
    }
  }, [daData]);

  // Pré-remplissage des champs buyer (hors adresse) quand orgInDb change
  useEffect(() => {
    if (!isReadOnly && orgInDb && acquirerType === 'self') {
      const buyerFields: Partial<FormValues> = {};
      if ((orgInDb.legal_entity || orgInDb.name) && !formMethods.getValues('buyerCompanyName'))
        buyerFields.buyerCompanyName = orgInDb.legal_entity || orgInDb.name;
      if (orgInDb.siret && !formMethods.getValues('buyerSiret')) buyerFields.buyerSiret = orgInDb.siret;
      if (orgInDb.town && !formMethods.getValues('buyerCity')) buyerFields.buyerCity = orgInDb.town;
      if (orgInDb.post_code && !formMethods.getValues('buyerPostalCode'))
        buyerFields.buyerPostalCode = orgInDb.post_code;
      if (orgInDb.country && !formMethods.getValues('buyerCountry')) buyerFields.buyerCountry = orgInDb.country;

      if (Object.keys(buyerFields).length > 0) {
        formMethods.reset({ ...formMethods.getValues(), ...buyerFields });
      }
    }
  }, [orgInDb, isReadOnly, acquirerType]);

  // Pré-remplissage asynchrone de l'adresse du nouveau titulaire (buyer).
  // POC "Bug adresse DA" : on privilégie les SOUS-CHAMPS STRUCTURÉS Pipedrive
  // (address_route / address_street_number), désormais quasi systématiques quand
  // l'adresse est validée Google Maps. Le re-parsing du texte libre `orgInDb.address`
  // (qui sortait « type = Inconnu » + nom pollué par « , ville, pays ») ne sert plus
  // que de filet pour le legacy résiduel non validé.
  useEffect(() => {
    if (isReadOnly || !orgInDb || acquirerType !== 'self') return;
    const buyerFields: Partial<FormValues> = {};

    if (hasStructuredAddress(orgInDb)) {
      // --- Chemin nominal : données structurées Pipedrive ---
      const derived = deriveAddressFromStructuredOrg({
        address_street_number: orgInDb.address_street_number,
        address_route: orgInDb.address_route,
        address_postal_code: orgInDb.address_postal_code ?? orgInDb.post_code,
        address_locality: orgInDb.address_locality ?? orgInDb.town,
        address_country: orgInDb.address_country ?? orgInDb.country,
      });
      if (derived.number && !formMethods.getValues('buyerAddressNumber'))
        buyerFields.buyerAddressNumber = derived.number;
      if (derived.typeCode && !formMethods.getValues('buyerAddressType'))
        buyerFields.buyerAddressType = derived.typeCode;
      if (derived.name && !formMethods.getValues('buyerAddressName')) buyerFields.buyerAddressName = derived.name;
    } else if (orgInDb.address) {
      // --- Fallback legacy : adresse en TEXTE LIBRE (non validée Google Maps) ---
      // parseFreeTextAddress isole le suffixe « , CP ville, pays » (donc plus de
      // ville/pays qui polluent le nom de voie) et remplit aussi CP/ville si absents.
      const p = parseFreeTextAddress(orgInDb.address);
      if (p.number && !formMethods.getValues('buyerAddressNumber')) buyerFields.buyerAddressNumber = p.number;
      if (p.typeCode && !formMethods.getValues('buyerAddressType')) buyerFields.buyerAddressType = p.typeCode;
      if (p.name && !formMethods.getValues('buyerAddressName')) buyerFields.buyerAddressName = p.name;
      if (p.postalCode && !formMethods.getValues('buyerPostalCode')) buyerFields.buyerPostalCode = p.postalCode;
      if (p.city && !formMethods.getValues('buyerCity')) buyerFields.buyerCity = p.city;
      if (p.country && !formMethods.getValues('buyerCountry')) buyerFields.buyerCountry = p.country;
    }

    if (Object.keys(buyerFields).length > 0) {
      formMethods.reset({ ...formMethods.getValues(), ...buyerFields });
    }
  }, [orgInDb?.address, orgInDb?.address_route, orgInDb?.address_street_number, isReadOnly, acquirerType]);

  // Initialiser acquirerType en mode lecture seule
  useEffect(() => {
    if (isReadOnly && daData) {
      // En mode lecture seule, on considère que c'est toujours "autre entité"
      // car les données viennent de la base de données
      setAcquirerType('other');
    }
  }, [isReadOnly, daData]);

  const handleBirthCityChange = async (
    _event: SyntheticEvent<Element, Event>,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ): Promise<void> => {
    if (value && value.length > 3) {
      try {
        const response: ApiResponse<CityData[]> = await makeAuthenticatedApiCall(
          'get',
          `/api/geo/cities/?searchParam=${value}&searchType=municipality`,
        );
        if (Array.isArray(response.data) && response.data.length > 0) {
          const options: CityOption[] = response.data.map((item: CityData) => ({
            label: item.name,
            value: item.postcode,
          }));
          setBirthDepartmentOptions(options);
        } else {
          setBirthDepartmentOptions([]);
        }
      } catch (error) {
        console.error('Error fetching city data:', error);
        setBirthDepartmentOptions([]);
      }
    } else {
      setBirthDepartmentOptions([]);
    }
  };

  const handlePostalCodeChange = async (event: any, value: string): Promise<void> => {
    if (value && value.length === 5) {
      try {
        const response: ApiResponse<CityData[]> = await makeAuthenticatedApiCall(
          'get',
          `/api/geo/pc/?searchParam=${value}`,
        );
        if (Array.isArray(response.data) && response.data.length > 0) {
          const filteredCities: CityOption[] = response.data.map((item: CityData) => ({
            label: item.name,
            value: item.postcode,
          }));
          setCityOptions(filteredCities);
        } else {
          setCityOptions([]);
        }
      } catch (error) {
        console.error('Error fetching city data:', error);
        setCityOptions([]);
      }
    } else {
      setCityOptions([]);
    }
  };

  // Add validation rules
  const validationRules = {
    regPlate: {
      required: 'Ce champ est requis',
      pattern: {
        value: /^(?:[A-Z]{2}-\d{3}-[A-Z]{2})|(?:\d{3} [A-Z]{3} \d{2})$/,
        message:
          "Le format de la plaque n'est pas valide. S'il s'agit d'une ancienne plaque, vous pouvez utiliser le formulaire suivant : https://form.typeform.com/to/Q4vakeKH",
      },
      minLength: {
        value: 7,
        message: "L'immatriculation doit contenir au moins 7 caractères hors tirets et espaces",
      },
    },
    vin: { required: 'Ce champ est requis' },
    numFormule: { required: 'Ce champ est requis' },
    sellerFirstName: { required: 'Ce champ est requis' },
    sellerLastName: { required: 'Ce champ est requis' },

    addressNumber: {
      validate: (value: string) => {
        const addressType = watch('buyerAddressType');
        if (addressType !== 'ldit' && !value) {
          return 'Ce champ est requis';
        }
        return true;
      },
    },
    addressType: { required: 'Ce champ est requis' },
    addressName: { required: 'Ce champ est requis' },
    city: { required: 'Ce champ est requis' },
    postalCode: { required: 'Ce champ est requis' },
    country: { required: 'Ce champ est requis' },
    sellerSiret: { required: 'Ce champ est requis' },
    sellerCompanyName: { required: 'Ce champ est requis' },
    buyerSiret: { required: 'Ce champ est requis' },
    buyerCompanyName: { required: 'Ce champ est requis' },
    buyerPlaceOfBirth: { required: 'Ce champ est requis' },
    purchaseDateTime: {
      required: 'Ce champ est requis',
      validate: (value: string) => {
        if (!value) return 'Ce champ est requis';
        const date = new Date(value);
        if (isNaN(date.getTime())) return 'Format de date invalide';
        const year = date.getFullYear();
        const currentYear = new Date().getFullYear();
        if (year < 2000 || year > currentYear + 1) {
          return "L'année ne semble pas être au bon format";
        }
        return true;
      },
    },
    buyerDateOfBirth: {
      required: 'Ce champ est requis',
      validate: (value: string) => {
        if (!value) return 'Ce champ est requis';
        const date = new Date(value);
        if (isNaN(date.getTime())) return 'Format de date invalide';
        const today = new Date();
        const minDate = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate());
        const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
        if (date > maxDate) return "L'acheteur doit avoir au moins 18 ans";
        if (date < minDate) return 'La date de naissance semble incorrecte';
        return true;
      },
    },
  };

  const openSnackbar = (message: string, severity: AlertColor) => {
    setSnackbarMessage(message);
    setSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleConfirmClick = async () => {
    await trigger();

    // Les vérifications sont maintenant faites en continu avec isFormCompletelyValid
    if (isFormCompletelyValid) {
      // 🎯 Tracking Sentry pour la soumission DA
      daBreadcrumb('DA Submission Started', 'da_submission_started', {
        regPlate: formMethods.getValues('regPlate'),
        vin: formMethods.getValues('vin'),
        sellerClientType: formMethods.getValues('sellerClientType'),
        buyerCompanyName: formMethods.getValues('buyerCompanyName'),
        acquirerType,
        certificateUrlsCount: certificateUrls.length,
        cerfaAchatUrlsCount: cerfaAchatUrls.length,
        cerfaCessionUrlsCount: cerfaCessionUrls.length,
        mandatUrlsCount: mandatUrls.length,
        kbisUrlsCount: kbisUrls.length,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      });
      Sentry.setTag('da_submission', 'true');
      Sentry.startSession(); // Force une nouvelle session

      setIsConfirmModalOpen(true);
    } else {
      // Ce cas ne devrait plus arriver puisque le bouton est désactivé
      openSnackbar('Des informations sont manquantes ou incorrectes. Veuillez vérifier le formulaire.', 'error');
    }
  };

  const handleFinalConfirm = async () => {
    // Protection renforcée contre les soumissions multiples
    if (isLoading || isSubmitting) {
      console.log('Soumission déjà en cours, ignorée');

      // 🎯 Tracking Sentry pour les tentatives multiples
      Sentry.withScope((scope) => {
        scope.setTag('action', 'da_submission_duplicate_attempt');
        scope.setTag('form_type', 'declaration_achat');
        scope.setExtras({
          isLoading,
          isSubmitting,
          regPlate: formMethods.getValues('regPlate'),
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        });
        Sentry.captureMessage('DA Submission Duplicate Attempt Blocked', 'warning');
      });

      return;
    }

    // Variable pour tracker si une DA a été créée (pour cleanup en cas d'erreur)
    let createdRegistrationId: number | null = null;

    try {
      setIsLoading(true);
      setIsSubmitting(true); // Protection renforcée
      setProgress(0);
      setCurrentStep('Initialisation');

      // 🎯 Tracking Sentry pour le début de soumission
      daBreadcrumb('DA Submission Final Confirm Started', 'da_submission_final_confirm', {
        regPlate: formMethods.getValues('regPlate'),
        vin: formMethods.getValues('vin'),
        sellerClientType: formMethods.getValues('sellerClientType'),
        buyerCompanyName: formMethods.getValues('buyerCompanyName'),
        acquirerType,
        certificateUrlsCount: certificateUrls.length,
        cerfaAchatUrlsCount: cerfaAchatUrls.length,
        cerfaCessionUrlsCount: cerfaCessionUrls.length,
        mandatUrlsCount: mandatUrls.length,
        kbisUrlsCount: kbisUrls.length,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      });

      const formData = formMethods.getValues();

      // Le statut initial sera déterminé côté backend selon le type de facturation
      // Plus besoin de logique frontend pour cela
      const status = 'DA_REQUEST'; // Statut temporaire, sera mis à jour par le backend

      // 1. Create DA registration
      setCurrentStep("Création de la demande de déclaration d'achat");

      // 🎯 Tracking Sentry pour l'étape 1
      daBreadcrumb('DA Step 1: Registration Creation Started', 'da_step_1_registration', {
        regPlate: formData.regPlate,
        vin: formData.vin,
        numFormule: formData.numFormule,
        purchaseDateTime: formData.purchaseDateTime,
        status,
        orgId: currentUser?.data?.organization?.value,
        personId: currentUser?.data?.id,
        timestamp: new Date().toISOString(),
      });

      const registrationResponse = await makeAuthenticatedApiCall<RegistrationResponse>(
        'post',
        '/api/da/registrations',
        {
          regPlate: formData.regPlate,
          vin: formData.vin,
          numFormule: formData.numFormule,
          purchaseDateTime: formData.purchaseDateTime,
          status: status,
          orgId: currentUser?.data?.organization?.value,
          personId: currentUser?.data?.id,
        },
      );
      const registrationId = registrationResponse.data.registrationId;
      createdRegistrationId = registrationId; // Tracker pour cleanup en cas d'erreur
      setProgress(20);

      // 🎯 Tracking Sentry pour la réussite de l'étape 1
      daBreadcrumb('DA Step 1: Registration Created Successfully', 'da_step_1_registration_success', {
        registrationId,
        regPlate: formData.regPlate,
        timestamp: new Date().toISOString(),
      });

      // 2. Create seller record
      setCurrentStep('Enregistrement des informations du vendeur');

      // 🎯 Tracking Sentry pour l'étape 2
      daBreadcrumb('DA Step 2: Seller Creation Started', 'da_step_2_seller', {
        registrationId,
        regPlate: formData.regPlate,
        sellerClientType: formData.sellerClientType,
        sellerFirstName: formData.sellerFirstName,
        sellerLastName: formData.sellerLastName,
        sellerSiret: formData.sellerSiret,
        sellerCompanyName: formData.sellerCompanyName,
        certificateUrlsCount: certificateUrls.length,
        cerfaAchatUrlsCount: cerfaAchatUrls.length,
        cerfaCessionUrlsCount: cerfaCessionUrls.length,
        mandatUrlsCount: mandatUrls.length,
        recepisseUrlsCount: recepisseUrls.length,
        timestamp: new Date().toISOString(),
      });

      await makeAuthenticatedApiCall('post', `/api/da/sellers/${registrationId}`, {
        clientType: formData.sellerClientType,
        firstName: formData.sellerFirstName,
        lastName: formData.sellerLastName,
        siret: formData.sellerSiret,
        companyName: formData.sellerCompanyName,
        certificateUrls,
        cerfaAchatUrls,
        cerfaCessionUrls,
        mandatUrls,
        recepisseUrls,
      });
      setProgress(40);

      // 🎯 Tracking Sentry pour la réussite de l'étape 2
      daBreadcrumb('DA Step 2: Seller Created Successfully', 'da_step_2_seller_success', {
        registrationId,
        regPlate: formData.regPlate,
        timestamp: new Date().toISOString(),
      });

      // 3. Create buyer record (professionnel uniquement)
      setCurrentStep("Enregistrement des informations de l'acquéreur");

      // 🎯 Tracking Sentry pour l'étape 3
      daBreadcrumb('DA Step 3: Buyer Creation Started', 'da_step_3_buyer', {
        registrationId,
        regPlate: formData.regPlate,
        buyerCompanyName: formData.buyerCompanyName,
        buyerSiret: formData.buyerSiret,
        buyerCity: formData.buyerCity,
        buyerPostalCode: formData.buyerPostalCode,
        acquirerType,
        kbisUrlsCount: kbisUrls.length,
        buyerFilesUrlsCount: buyerFilesUrls.length,
        buyerResidencyFilesUrlsCount: buyerResidencyFilesUrls.length,
        timestamp: new Date().toISOString(),
      });

      await makeAuthenticatedApiCall('post', `/api/da/buyers/${registrationId}`, {
        clientType: 'pro',
        addressNumber: formData.buyerAddressNumber,
        addressExtension: formData.buyerAddressExtension,
        addressType: formData.buyerAddressType,
        addressName: formData.buyerAddressName,
        addressPlace: formData.buyerAddressPlace,
        addressStairs: formData.buyerAddressStairs,
        addressComp: formData.buyerAddressComp,
        addressPostalBox: formData.buyerAddressPostalBox,
        city: formData.buyerCity,
        postalCode: formData.buyerPostalCode,
        country: formData.buyerCountry,
        siret: formData.buyerSiret,
        companyName: formData.buyerCompanyName,
        files: acquirerType === 'other' ? kbisUrls.join(',') : buyerFilesUrls.join(','),
        residencyFiles: buyerResidencyFilesUrls.join(','),
      });
      setProgress(60);

      // 🎯 Tracking Sentry pour la réussite de l'étape 3
      daBreadcrumb('DA Step 3: Buyer Created Successfully', 'da_step_3_buyer_success', {
        registrationId,
        regPlate: formData.regPlate,
        timestamp: new Date().toISOString(),
      });

      // 4. Generate invoice
      setCurrentStep('Génération de la facture');

      // 🎯 Tracking Sentry pour l'étape 4
      daBreadcrumb('DA Step 4: Invoice Generation Started', 'da_step_4_invoice', {
        registrationId,
        regPlate: formData.regPlate,
        buyerCompanyName: formData.buyerCompanyName,
        buyerSiret: formData.buyerSiret,
        agencyID: currentUser?.data?.organization?.value,
        personID: currentUser?.data?.id,
        timestamp: new Date().toISOString(),
      });

      const agencyData = {
        agencyID: currentUser?.data?.organization?.value,
        personID: currentUser?.data?.id,
        agencyEmail:
          currentUser?.data?.organization_email || // Priorité : email de l'organisation depuis la BDD
          currentUser?.data?.primary_email || // Fallback : email principal de Pipedrive
          currentUser?.data?.email, // Fallback final : email de l'utilisateur
        agencyName: currentUser?.data?.organization?.name,
        agencyAddress: currentUser?.data?.organization?.address,
        agencySiret: currentUser?.data?.organization?.siret || null, // Ajouter le SIRET de l'organisation
      };

      // Adapter les données au format attendu par l'API Qonto (professionnel uniquement)
      const dealData = {
        id: registrationId,
        companyName: formData.buyerCompanyName,
        siret: formData.buyerSiret,
        addressNumber: formData.buyerAddressNumber,
        addressStreet: formData.buyerAddressName,
        addressCity: formData.buyerCity,
        addressPostalCode: formData.buyerPostalCode,
        addressCountry: formData.buyerCountry,
      };

      const qontoResponse = await makeAuthenticatedApiCall<QontoResponse>('post', '/api/qonto/generate', {
        pipeline: 'da',
        deal: dealData,
        vat: 0.2,
        sellingPrice: '10',
        regPlate: formData.regPlate,
        agencyData,
      });
      setProgress(100);

      // 🎯 Tracking Sentry pour la réussite de l'étape 4
      daBreadcrumb('DA Step 4: Invoice Generated Successfully', 'da_step_4_invoice_success', {
        registrationId,
        regPlate: formData.regPlate,
        qontoSuccess: qontoResponse.data.success,
        qontoMessage: qontoResponse.data.message,
        hasInvoiceUrl: !!qontoResponse.data.invoiceFirebaseUrl,
        timestamp: new Date().toISOString(),
      });

      // Vérifier si la réponse contient un message de succès ou une URL de facture
      if (qontoResponse.data.success || qontoResponse.data.message || qontoResponse.data.invoiceFirebaseUrl) {
        setCurrentStep('Demande enregistrée avec succès');

        // 🎯 Tracking Sentry pour le succès final
        daBreadcrumb('DA Submission Completed Successfully', 'da_submission_complete_success', {
          registrationId,
          regPlate: formData.regPlate,
          qontoSuccess: qontoResponse.data.success,
          qontoMessage: qontoResponse.data.message,
          hasInvoiceUrl: !!qontoResponse.data.invoiceFirebaseUrl,
          totalDuration: new Date().toISOString(), // Pour calculer la durée totale
          timestamp: new Date().toISOString(),
        });

        // Feedback visuel immédiat et fort
        openSnackbar('✅ Demande enregistrée avec succès !', 'success');

        // Redirection immédiate (pas de setTimeout)
        navigate('/list-da', {
          state: {
            showSuccessMessage: true,
            message: "Votre déclaration d'achat a été enregistrée avec succès !",
          },
        });
      }
    } catch (error: any) {
      console.error('Error in handleFinalConfirm:', error);

      // Extraire le message d'erreur du backend s'il existe
      const backendMessage = error.response?.data?.message || error.response?.data?.error;
      const errorStatus = error.response?.status;

      // 🎯 Tracking Sentry pour les erreurs
      Sentry.withScope((scope) => {
        scope.setTag('action', 'da_submission_error');
        scope.setTag('form_type', 'declaration_achat');
        scope.setTag('error_type', errorStatus === 409 ? 'duplicate_submission' : 'general_error');
        scope.setTag('error_status', String(errorStatus));
        scope.setExtras({
          regPlate: formMethods.getValues('regPlate'),
          currentStep,
          errorStatus,
          errorMessage: error.message,
          backendMessage,
          errorResponse: error.response?.data,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        });
        Sentry.captureException(error);
      });

      // CLEANUP: Si une DA a été créée mais que l'invoice a échoué, la marquer invisible
      if (createdRegistrationId) {
        try {
          await makeAuthenticatedApiCall('patch', `/api/da/${createdRegistrationId}/visibility`, {
            visible: false,
          });

          // 🎯 Tracking Sentry pour le cleanup réussi
          Sentry.withScope((scope) => {
            scope.setTag('action', 'da_cleanup_success');
            scope.setTag('form_type', 'declaration_achat');
            scope.setExtras({
              registrationId: createdRegistrationId,
              regPlate: formMethods.getValues('regPlate'),
              vin: formMethods.getValues('vin'),
              agencyEmail:
                currentUser?.data?.organization_email ||
                currentUser?.data?.primary_email ||
                currentUser?.data?.email,
              agencyName: currentUser?.data?.organization?.name,
              agencyId: currentUser?.data?.organization?.value,
              failedAtStep: currentStep,
              originalError: error.message,
              timestamp: new Date().toISOString(),
            });
            Sentry.captureMessage('DA marked invisible after failed submission', 'warning');
          });
        } catch (cleanupError) {
          // 🎯 Tracking Sentry pour l'échec du cleanup
          Sentry.withScope((scope) => {
            scope.setTag('action', 'da_cleanup_failed');
            scope.setTag('form_type', 'declaration_achat');
            scope.setExtras({
              registrationId: createdRegistrationId,
              regPlate: formMethods.getValues('regPlate'),
              vin: formMethods.getValues('vin'),
              agencyEmail:
                currentUser?.data?.organization_email ||
                currentUser?.data?.primary_email ||
                currentUser?.data?.email,
              agencyName: currentUser?.data?.organization?.name,
              agencyId: currentUser?.data?.organization?.value,
              failedAtStep: currentStep,
              originalError: error.message,
              cleanupError: (cleanupError as Error).message,
              timestamp: new Date().toISOString(),
            });
            Sentry.captureException(cleanupError);
          });
        }
      }

      // Marquer qu'une erreur s'est produite pour désactiver le bouton
      setHasSubmissionError(true);

      // Fermer la modale pour montrer le snackbar d'erreur
      setIsConfirmModalOpen(false);

      // Gestion spécifique des erreurs selon le code HTTP et le message backend
      if (errorStatus === 409) {
        openSnackbar('Une création de DA est déjà en cours avec ces données. Veuillez patienter.', 'warning');
      } else if (backendMessage) {
        // Afficher le message d'erreur du backend s'il existe (contient déjà le contact support)
        openSnackbar(backendMessage, 'error');
      } else {
        // Message par défaut avec contact support
        openSnackbar(
          `Une erreur est survenue à l'étape "${currentStep}". Veuillez contacter le support : luc@aptekaa.fr`,
          'error',
        );
      }
    } finally {
      setIsLoading(false);
      setIsSubmitting(false); // Réinitialiser la protection renforcée
    }
  };

  // File handling functions
  const certificateUploadComplete = (url: string) => {
    setCertificateUrls((prev) => [...prev, url]);
  };

  const certificateDeleteComplete = (url: string) => {
    setCertificateUrls((prev) => prev.filter((u) => u !== url));
  };

  const cerfaAchatUploadComplete = (url: string) => {
    setCerfaAchatUrls((prev) => [...prev, url]);
  };

  const cerfaAchatDeleteComplete = (url: string) => {
    setCerfaAchatUrls((prev) => prev.filter((u) => u !== url));
  };

  const cerfaCessionUploadComplete = (url: string) => {
    setCerfaCessionUrls((prev) => [...prev, url]);
  };

  const cerfaCessionDeleteComplete = (url: string) => {
    setCerfaCessionUrls((prev) => prev.filter((u) => u !== url));
  };

  const mandatUploadComplete = (url: string) => {
    setMandatUrls((prev) => [...prev, url]);
  };

  const mandatDeleteComplete = (url: string) => {
    setMandatUrls((prev) => prev.filter((u) => u !== url));
  };

  const recepisseUploadComplete = (url: string) => {
    setRecepisseUrls((prev) => [...prev, url]);
  };

  const recepisseDeleteComplete = (url: string) => {
    setRecepisseUrls((prev) => prev.filter((u) => u !== url));
  };

  // Fonctions pour gérer les fichiers KBIS
  const kbisUploadComplete = (url: string) => {
    setKbisUrls([url]); // Un seul KBIS autorisé, donc on remplace
  };

  const kbisDeleteComplete = (url: string) => {
    setKbisUrls([]);
  };

  // Fonctions pour gérer le type d'acquéreur
  const handleAcquirerTypeChange = (type: 'self' | 'other') => {
    setAcquirerType(type);

    if (type === 'self') {
      // Remettre les données de l'organisation
      if (orgInDb) {
        const buyerFields: Partial<FormValues> = {
          buyerCompanyName: orgInDb.legal_entity || orgInDb.name || '',
          buyerSiret: orgInDb.siret || '',
          buyerCity: orgInDb.town || '',
          buyerPostalCode: orgInDb.post_code || '',
          buyerCountry: orgInDb.country || '',
        };

        // Adresse : sous-champs STRUCTURÉS Pipedrive d'abord (comme le useEffect de
        // prefill), fallback re-parsing du texte libre pour le legacy non validé.
        if (hasStructuredAddress(orgInDb)) {
          const d = deriveAddressFromStructuredOrg({
            address_street_number: orgInDb.address_street_number,
            address_route: orgInDb.address_route,
            address_postal_code: orgInDb.address_postal_code ?? orgInDb.post_code,
            address_locality: orgInDb.address_locality ?? orgInDb.town,
            address_country: orgInDb.address_country ?? orgInDb.country,
          });
          buyerFields.buyerAddressNumber = d.number;
          buyerFields.buyerAddressType = d.typeCode;
          buyerFields.buyerAddressName = d.name;
        } else if (orgInDb.address) {
          Object.assign(buyerFields, parseOrgAddress(orgInDb.address));
        }

        formMethods.reset({ ...formMethods.getValues(), ...buyerFields });
      }
      // Vider les fichiers KBIS car pas nécessaires pour "Je suis l'acquéreur"
      setKbisUrls([]);
    } else {
      // Remettre à zéro les données du nouveau titulaire (professionnel uniquement)
      const emptyBuyerFields: Partial<FormValues> = {
        buyerCompanyName: '',
        buyerSiret: '',
        buyerAddressNumber: '',
        buyerAddressExtension: '',
        buyerAddressType: '',
        buyerAddressName: '',
        buyerAddressPlace: '',
        buyerAddressStairs: '',
        buyerAddressComp: '',
        buyerAddressPostalBox: '',
        buyerCity: '',
        buyerPostalCode: '',
        buyerCountry: '',
      };

      formMethods.reset({ ...formMethods.getValues(), ...emptyBuyerFields });
      // Garder les fichiers KBIS existants si on passe en mode "Autre entité"
    }
  };

  // Parse une adresse en texte libre (fallback legacy, adresse non validée Gmaps).
  // Délègue au parseur partagé qui isole « , CP ville, pays » et découpe type/nom.
  const parseOrgAddress = (address: string): Partial<FormValues> => {
    const p = parseFreeTextAddress(address);
    const buyerFields: Partial<FormValues> = {};
    if (p.number) buyerFields.buyerAddressNumber = p.number;
    if (p.typeCode) buyerFields.buyerAddressType = p.typeCode;
    if (p.name) buyerFields.buyerAddressName = p.name;
    if (p.postalCode) buyerFields.buyerPostalCode = p.postalCode;
    if (p.city) buyerFields.buyerCity = p.city;
    if (p.country) buyerFields.buyerCountry = p.country;
    return buyerFields;
  };

  console.log(errors);

  return (
    <Container maxWidth={false}>
      <Box
        sx={{
          margin: { xs: '2em 1em', md: '2em 3em' },
          backgroundColor: '#f5f5f5',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          display: registrationIdParam ? 'none' : 'block',
        }}
      >
        <Typography variant="body1" align="center" gutterBottom>
          📝 Comment faire votre demande de Déclaration d'Achat ?
        </Typography>
        <Typography variant="body2" align="center" gutterBottom>
          Suivez ces étapes simples pour soumettre votre Déclaration d'Achat et obtenir rapidement votre récépissé :
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              secondary={
                <span style={{ color: 'black' }}>
                  <strong>1️⃣ Remplissez le formulaire : </strong> Vérifiez bien que toutes les informations sont exactes
                  ✅. Plus votre dossier est complet et juste, plus il sera traité rapidement !
                </span>
              }
            />
          </ListItem>
          <ListItem>
            <ListItemText
              secondary={
                <span style={{ color: 'black' }}>
                  <strong>2️⃣ Réglez la facture : </strong> Une fois le dossier soumis, une facture sera générée 💸.
                  Réglez-la pour que nous puissions traiter votre demande. (Si vous êtes en facturation
                  quinzaine/mensuelle, vous pouvez ignorer cette étape)
                </span>
              }
            />
          </ListItem>
          <ListItem>
            <ListItemText
              secondary={
                <span style={{ color: 'black' }}>
                  <strong>3️⃣ Traitement de votre dossier : </strong> Dès réception du paiement, notre équipe prend en
                  charge votre demande ⚙️ et la traite dans les meilleurs délais.
                </span>
              }
            />
          </ListItem>
          <ListItem>
            <ListItemText
              secondary={
                <span style={{ color: 'black' }}>
                  <strong>4️⃣ Récépissé immédiat : </strong> Recevez immédiatement votre{' '}
                  <strong>Récépissé de Déclaration d'Achat</strong> 📄.
                </span>
              }
            />
          </ListItem>
          <ListItem>
            <ListItemText
              secondary={
                <span style={{ color: 'black' }}>
                  <strong>5️⃣ Suivi et historique : </strong> Suivez facilement toutes vos demandes via notre interface
                  👀. Retrouvez vos récépissés et factures dans l'onglet{' '}
                  <a href="/list-da" style={{ color: 'black', textDecoration: 'underline' }} rel="noopener noreferrer">
                    <strong>Liste des DA</strong> 🗂️
                  </a>
                  .
                </span>
              }
            />
          </ListItem>
        </List>
        <Typography variant="body2" align="center" gutterBottom mt={4}>
          Une question ? Notre équipe est là pour vous accompagner à chaque étape ! 🚀
          <br />
          <a href="mailto:skipcarcg@mail.com" style={{ color: 'black', textDecoration: 'underline' }}>
            skipcarcg@mail.com
          </a>
        </Typography>
      </Box>
      <Typography
        variant="h1"
        align="center"
        sx={{ color: '#979ED2', margin: { xs: '0.5em auto 1em', md: '1em auto' } }}
      >
        Faire votre demande de déclaration d'achat
      </Typography>
      <Paper sx={{ marginTop: 1, marginBottom: { xs: 4, md: 0 } }} elevation={0}>
        <Box sx={{ p: { xs: 0, md: 4 } }}>
          <FormProvider {...formMethods}>
            <form>
              <Grid container>
                {/* Section Véhicule */}
                <FormLabel component="legend">Véhicule</FormLabel>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="purchaseDateTime"
                      control={control}
                      rules={validationRules.purchaseDateTime}
                      defaultValue=""
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          disabled={isReadOnly}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          margin="dense"
                          className="inputElement"
                          id="purchaseDateTime"
                          label="Date et heure de la vente"
                          type="datetime-local"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          inputProps={{
                            min: '2000-01-01T00:00',
                            max: '3000-01-01T00:00',
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="regPlate"
                      control={control}
                      rules={validationRules.regPlate}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          disabled={isReadOnly}
                          className="inputElement"
                          {...field}
                          inputProps={{ maxLength: 9 }}
                          value={regPlateValue}
                          helperText={
                            errors.regPlate?.message ? (
                              <span>
                                {errors.regPlate.message.includes('https://') ? (
                                  <>
                                    {errors.regPlate.message.split('https://')[0]}
                                    <a
                                      href={`https://${errors.regPlate.message.split('https://')[1]}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: 'inherit', textDecoration: 'underline' }}
                                    >
                                      https://{errors.regPlate.message.split('https://')[1]}
                                    </a>
                                  </>
                                ) : (
                                  errors.regPlate.message
                                )}
                              </span>
                            ) : null
                          }
                          onChange={(e) => {
                            const upperCaseValue = e.target.value.toUpperCase();
                            field.onChange(formatRegPlate(upperCaseValue));
                          }}
                          error={!!errors?.regPlate?.message}
                          margin="dense"
                          label="Immatriculation"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="vin"
                      control={control}
                      rules={validationRules.vin}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          disabled={isReadOnly}
                          {...field}
                          error={!!errors.vin}
                          margin="dense"
                          className="inputElement"
                          id="vin"
                          label="Numéro VIN"
                          type="text"
                          fullWidth
                          inputProps={{ maxLength: 17 }}
                          onChange={(e) => field.onChange(removeSpaces(e.target.value))}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="numFormule"
                      control={control}
                      rules={validationRules.numFormule}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          disabled={isReadOnly}
                          {...field}
                          error={!!errors.numFormule}
                          margin="dense"
                          className="inputElement"
                          id="numFormule"
                          label="Numéro de Formule"
                          type="text"
                          fullWidth
                          inputProps={{ maxLength: 11 }}
                          onChange={(e) => field.onChange(removeSpaces(e.target.value))}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Typography variant="body1" align="center" gutterBottom mt={4} mb={3}>
                      Documents requis pour la déclaration d'achat :
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" component="div" textAlign={'center'}>
                          1 - Carte grise avec la mention "cédée le ../../.."" et la signature du titulaire et du
                          cotitulaire
                        </Typography>
                        {isReadOnly && certificateUrls.length > 0 ? (
                          <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={certificateUrls.join(',')} />
                        ) : (
                          <FirebaseUploadComponent
                            onUploadComplete={certificateUploadComplete}
                            onDeleteComplete={certificateDeleteComplete}
                            uploadedUrls={certificateUrls}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" component="div" textAlign={'center'}>
                          2 - Cerfa de déclaration d'achat{' '}
                          <a
                            href="https://mobile.interieur.gouv.fr/content/download/33523/250599/file/13751-2_statique.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#fdd15f', textDecoration: 'underline' }}
                          >
                            (Cerfa 13751*02)
                          </a>{' '}
                          complété par le vendeur et l'acquéreur
                          <br />
                        </Typography>
                        {isReadOnly && cerfaAchatUrls.length > 0 ? (
                          <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={cerfaAchatUrls.join(',')} />
                        ) : (
                          <FirebaseUploadComponent
                            onUploadComplete={cerfaAchatUploadComplete}
                            onDeleteComplete={cerfaAchatDeleteComplete}
                            uploadedUrls={cerfaAchatUrls}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" component="div" textAlign={'center'}>
                          3 - Cerfa de déclaration de cession{' '}
                          <a
                            href="https://immatriculation.ants.gouv.fr/files/1e4341b6-4695-4f74-a51a-5ef962fba392/ef4ec059-07a7-4cc7-804f-e16944afeb5e/87e1c701-91dd-49de-86ed-52b7cb406dd0/cerfa_15776-01.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#fdd15f', textDecoration: 'underline' }}
                          >
                            (Cerfa 15776*01)
                          </a>{' '}
                          complété par le vendeur et l' acquéreur
                          <br />
                        </Typography>
                        {isReadOnly && cerfaCessionUrls.length > 0 ? (
                          <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={cerfaCessionUrls.join(',')} />
                        ) : (
                          <FirebaseUploadComponent
                            onUploadComplete={cerfaCessionUploadComplete}
                            onDeleteComplete={cerfaCessionDeleteComplete}
                            uploadedUrls={cerfaCessionUrls}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" component="div" textAlign={'center'}>
                          4 - Mandat à un professionnel{' '}
                          <a
                            href="https://www.service-public.fr/simulateur/calcul/13757"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#fdd15f', textDecoration: 'underline' }}
                          >
                            (Cerfa 13757*03)
                          </a>
                          <br />
                        </Typography>
                        {isReadOnly && mandatUrls.length > 0 ? (
                          <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={mandatUrls.join(',')} />
                        ) : (
                          <FirebaseUploadComponent
                            onUploadComplete={mandatUploadComplete}
                            onDeleteComplete={mandatDeleteComplete}
                            uploadedUrls={mandatUrls}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" component="div" textAlign={'center'}>
                          5 - Récépissé DA (non obligatoire)
                        </Typography>
                        {isReadOnly && recepisseUrls.length > 0 ? (
                          <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={recepisseUrls.join(',')} />
                        ) : (
                          <FirebaseUploadComponent
                            onUploadComplete={recepisseUploadComplete}
                            onDeleteComplete={recepisseDeleteComplete}
                            uploadedUrls={recepisseUrls}
                          />
                        )}
                      </Grid>
                    </Grid>
                    {errors.sellerFilesUrls && typeof errors.sellerFilesUrls.message === 'string' && (
                      <Typography color="error" align="center">
                        {errors.sellerFilesUrls.message}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                <Divider
                  variant="fullWidth"
                  component="hr"
                  style={{
                    marginLeft: 24,
                    backgroundColor: '#fdd15f',
                    height: '2px',
                    width: 'calc(100% - 24px)',
                    border: 0,
                    marginTop: 30,
                    marginBottom: 30,
                  }}
                />

                {/* Section Ancien titulaire */}
                <FormLabel component="legend">Ancien titulaire</FormLabel>
                <Grid container alignItems="center" item xs={12} md={12}>
                  <Grid item>
                    <Typography variant="body2" component="div" sx={{ marginRight: 2 }}>
                      L'ancien propriétaire est un :
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Controller
                      name="sellerClientType"
                      control={control}
                      defaultValue="person"
                      render={({ field }) => (
                        <RadioGroup {...field} row>
                          <FormControlLabel
                            value="person"
                            control={<Radio disabled={isReadOnly} />}
                            label="Particulier"
                            className="inputElement"
                            disabled={isReadOnly}
                          />
                          <FormControlLabel
                            value="pro"
                            control={<Radio disabled={isReadOnly} />}
                            label="Pro"
                            className="inputElement"
                            disabled={isReadOnly}
                          />
                        </RadioGroup>
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Section Ancien titulaire - Détails */}
                <Grid container spacing={2}>
                  {sellerClientType === 'person' && (
                    <>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="sellerLastName"
                          control={control}
                          rules={sellerClientType === 'person' ? validationRules.sellerLastName : {}}
                          defaultValue=""
                          render={({ field }) => (
                            <TextField
                              disabled={isReadOnly}
                              {...field}
                              id="sellerLastName"
                              error={!!errors.sellerLastName}
                              className="inputElement"
                              margin="dense"
                              label="Nom"
                              type="text"
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="sellerFirstName"
                          control={control}
                          rules={sellerClientType === 'person' ? validationRules.sellerFirstName : {}}
                          defaultValue=""
                          render={({ field }) => (
                            <TextField
                              disabled={isReadOnly}
                              {...field}
                              error={!!errors.sellerFirstName}
                              className="inputElement"
                              margin="dense"
                              id="sellerFirstName"
                              label="Prénom"
                              type="text"
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                    </>
                  )}
                  {sellerClientType === 'pro' && (
                    <>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="sellerSiret"
                          control={control}
                          rules={{
                            required: 'Ce champ est requis',
                            validate: (value: string) => {
                              if (!value) return 'Ce champ est requis';
                              const cleanedValue = cleanSiret(value);
                              if (cleanedValue.length > 14) return 'Le SIRET ne peut pas dépasser 14 chiffres';
                              return validateSiret(value);
                            },
                          }}
                          defaultValue=""
                          render={({ field }) => (
                            <TextField
                              disabled={isReadOnly}
                              {...field}
                              className="inputElement"
                              error={!!errors.sellerSiret}
                              helperText={errors.sellerSiret?.message}
                              margin="dense"
                              id="sellerSiret"
                              label="SIRET"
                              type="text"
                              fullWidth
                              inputProps={{ maxLength: 17 }}
                              onChange={(e) => {
                                const formattedValue = formatSiret(e.target.value);
                                const cleanedValue = cleanSiret(formattedValue);
                                if (cleanedValue.length <= 14) {
                                  e.target.value = formattedValue;
                                  field.onChange(cleanedValue);
                                }
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="sellerCompanyName"
                          control={control}
                          rules={sellerClientType === 'pro' ? validationRules.sellerCompanyName : {}}
                          defaultValue=""
                          render={({ field }) => (
                            <TextField
                              disabled={isReadOnly}
                              {...field}
                              margin="dense"
                              error={!!errors.sellerCompanyName}
                              id="sellerCompanyName"
                              className="inputElement"
                              label="Raison sociale"
                              type="text"
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>

                <Divider
                  variant="fullWidth"
                  component="hr"
                  style={{
                    marginLeft: 24,
                    backgroundColor: '#fdd15f',
                    height: '2px',
                    width: 'calc(100% - 24px)',
                    border: 0,
                    marginTop: 30,
                    marginBottom: 30,
                  }}
                />

                {/* Section Nouveau titulaire */}
                <Grid container alignItems="center" item xs={12} md={12}>
                  <Grid item xs={12} md={6}>
                    <FormLabel component="legend">Nouveau titulaire</FormLabel>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={acquirerType === 'self'}
                          onChange={() => handleAcquirerTypeChange('self')}
                          disabled={isReadOnly}
                        />
                      }
                      label="Ma société est l'acquéreur du véhicule"
                      className="inputElement"
                    />
                    <FormControlLabel
                      control={
                        <Radio
                          checked={acquirerType === 'other'}
                          onChange={() => handleAcquirerTypeChange('other')}
                          disabled={isReadOnly}
                        />
                      }
                      label="Autre entité"
                      className="inputElement"
                    />
                  </Grid>
                </Grid>

                {/* Section Nouveau titulaire - Détails */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="buyerSiret"
                      control={control}
                      rules={{
                        required: 'Ce champ est requis',
                        validate: (value: string) => {
                          if (!value) return 'Ce champ est requis';
                          const cleanedValue = cleanSiret(value);
                          if (cleanedValue.length > 14) return 'Le SIRET ne peut pas dépasser 14 chiffres';
                          return validateSiret(value);
                        },
                      }}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          disabled={isReadOnly}
                          {...field}
                          className="inputElement"
                          error={!!errors.buyerSiret}
                          helperText={errors.buyerSiret?.message}
                          margin="dense"
                          id="buyerSiret"
                          label="SIRET"
                          type="text"
                          fullWidth
                          inputProps={{ maxLength: 17 }}
                          onChange={(e) => {
                            const formattedValue = formatSiret(e.target.value);
                            const cleanedValue = cleanSiret(formattedValue);
                            if (cleanedValue.length <= 14) {
                              e.target.value = formattedValue;
                              field.onChange(cleanedValue);
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="buyerCompanyName"
                      control={control}
                      rules={{ required: 'Ce champ est requis' }}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          disabled={isReadOnly}
                          {...field}
                          margin="dense"
                          error={!!errors.buyerCompanyName}
                          id="buyerCompanyName"
                          className="inputElement"
                          label="Raison sociale"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Adresse du nouveau titulaire */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={2}>
                    <Controller
                      name="buyerAddressNumber"
                      control={control}
                      rules={validationRules.addressNumber}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          disabled={isReadOnly}
                          {...field}
                          error={!!errors.buyerAddressNumber}
                          className="inputElement"
                          margin="dense"
                          id="buyerAddressNumber"
                          label="Numéro d'adresse"
                          type="number"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Controller
                      name="buyerAddressExtension"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          disabled={isReadOnly}
                          {...field}
                          className="inputElement"
                          margin="dense"
                          id="buyerAddressExtension"
                          label="Extension d'adresse"
                          type="text"
                          fullWidth
                          inputProps={{ maxLength: 7 }}
                          placeholder="Ex: BIS"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Controller
                      name="buyerAddressType"
                      control={control}
                      defaultValue=""
                      rules={{ required: 'Ce champ est requis' }}
                      render={({ field, fieldState: { error } }) => (
                        <FormControl disabled={isReadOnly} fullWidth margin="dense" error={!!error}>
                          <InputLabel className="inputElement" id={`${field.name}-label`}>
                            Type d'adresse
                          </InputLabel>
                          <Select
                            disabled={isReadOnly}
                            sx={{ minHeight: 'inherit', height: '56px' }}
                            className="inputElement"
                            {...field}
                            labelId={`${field.name}-label`}
                            id={field.name}
                            label="Type d'adresse"
                          >
                            <MenuItem value="rue">Rue</MenuItem>
                            <MenuItem value="all">Allée</MenuItem>
                            <MenuItem value="avn">Avenue</MenuItem>
                            <MenuItem value="blvd">Boulevard</MenuItem>
                            <MenuItem value="chau">Chaussée</MenuItem>
                            <MenuItem value="che">Chemin</MenuItem>
                            <MenuItem value="cour">Cour</MenuItem>
                            <MenuItem value="fbg">Faubourg</MenuItem>
                            <MenuItem value="fort">Fort</MenuItem>
                            <MenuItem value="imp">Impasse</MenuItem>
                            <MenuItem value="ldit">Lieu-Dit</MenuItem>
                            <MenuItem value="parc">Parc</MenuItem>
                            <MenuItem value="pass">Passage</MenuItem>
                            <MenuItem value="plc">Place</MenuItem>
                            <MenuItem value="quai">Quai</MenuItem>
                            <MenuItem value="rte">Route</MenuItem>
                            <MenuItem value="sent">Sentier</MenuItem>
                            <MenuItem value="squa">Square</MenuItem>
                            <MenuItem value="sans">Inconnu</MenuItem>
                          </Select>
                          {error && <FormHelperText>{error.message}</FormHelperText>}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="buyerAddressName"
                      control={control}
                      rules={validationRules.addressName}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          disabled={isReadOnly}
                          {...field}
                          className="inputElement"
                          error={!!errors.buyerAddressName}
                          margin="dense"
                          id="buyerAddressName"
                          label="Nom de la voie (Rue, Allée, ou Avenue, ne doivent pas apparaitre ici)"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  {/* Suite des champs d'adresse */}
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="buyerAddressPlace"
                      control={control}
                      rules={{}}
                      defaultValue=""
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          disabled={isReadOnly}
                          {...field}
                          className="inputElement"
                          error={!!error}
                          helperText={error ? error.message : ''}
                          margin="dense"
                          id="buyerAddressPlace"
                          label="Lieu-dit"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="buyerAddressStairs"
                      control={control}
                      rules={{}}
                      defaultValue=""
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          disabled={isReadOnly}
                          {...field}
                          className="inputElement"
                          error={!!error}
                          helperText={error ? error.message : ''}
                          margin="dense"
                          id="buyerAddressStairs"
                          label="Étage/Escalier/Appartement"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="buyerAddressComp"
                      control={control}
                      rules={{}}
                      defaultValue=""
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          disabled={isReadOnly}
                          {...field}
                          className="inputElement"
                          error={!!error}
                          helperText={error ? error.message : ''}
                          margin="dense"
                          id="buyerAddressComp"
                          label="Complément d'adresse"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="buyerAddressPostalBox"
                      control={control}
                      rules={{}}
                      defaultValue=""
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          disabled={isReadOnly}
                          {...field}
                          className="inputElement"
                          error={!!error}
                          helperText={error ? error.message : ''}
                          margin="dense"
                          id="buyerAddressPostalBox"
                          label="Boîte postale"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name="buyerPostalCode"
                      control={control}
                      rules={validationRules.postalCode}
                      defaultValue=""
                      render={({ field }) => (
                        <Autocomplete
                          freeSolo
                          disablePortal
                          disabled={isReadOnly}
                          className="inputElement"
                          options={cityOptions}
                          getOptionLabel={(option: any) => `${option.label} (${option.value})`}
                          isOptionEqualToValue={(option, value) => {
                            if (typeof value === 'string') {
                              return option.value === value;
                            }
                            if (typeof value === 'object' && value && 'value' in value) {
                              return option.value === value.value;
                            }
                            return false;
                          }}
                          onInputChange={(event, value) => handlePostalCodeChange(event, value)}
                          onChange={(event, value) => {
                            if (value && typeof value === 'object' && 'value' in value) {
                              const postalCode = value.value;
                              const city = value.label;
                              setSelectedPostalCode(postalCode);
                              setSelectedCity(city);
                              field.onChange(postalCode);
                              setValue('buyerCity', city);
                              setValue('buyerCountry', 'France'); // Met à jour automatiquement le champ "Pays"
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={{
                                minHeight: 'inherit',
                                height: '56px',
                                '& .MuiOutlinedInput-root': { height: '56px' },
                              }}
                              value={selectedPostalCode || field.value || ''}
                              onChange={(event) => {
                                setSelectedPostalCode(event.target.value);
                                field.onChange(event.target.value);
                              }}
                              error={!!errors.buyerPostalCode}
                              margin="dense"
                              id="buyerPostalCode"
                              label="Code postal"
                              type="text"
                              fullWidth
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name="buyerCity"
                      control={control}
                      rules={validationRules.city}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          disabled={isReadOnly}
                          {...field}
                          onChange={(event) => {
                            setSelectedCity(event.target.value);
                            field.onChange(event.target.value);
                          }}
                          error={!!errors.buyerCity}
                          className="inputElement"
                          margin="dense"
                          id="buyerCity"
                          label="Ville"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name="buyerCountry"
                      control={control}
                      rules={validationRules.country}
                      defaultValue="France"
                      render={({ field }) => (
                        <TextField
                          disabled={isReadOnly}
                          {...field}
                          className="inputElement"
                          error={!!errors.buyerCountry}
                          margin="dense"
                          id="buyerCountry"
                          label="Pays"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Section KBIS pour "Autre entité" */}
                {acquirerType === 'other' && (
                  <Grid container spacing={2} mt={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" component="div" textAlign={'center'} gutterBottom>
                        KBIS*
                      </Typography>
                      {isReadOnly && kbisUrls.length > 0 ? (
                        <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={kbisUrls.join(',')} />
                      ) : (
                        <FirebaseUploadComponent
                          onUploadComplete={kbisUploadComplete}
                          onDeleteComplete={kbisDeleteComplete}
                          uploadedUrls={kbisUrls}
                        />
                      )}
                      {acquirerType === 'other' && kbisUrls.length === 0 && (
                        <Typography color="error" align="center" variant="body2">
                          Le KBIS est obligatoire
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                )}

                {/* Section pour afficher les fichiers des commentaires externes */}
                {daData && daData.commentFilesUrls && (
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        📎 Fichiers des commentaires externes
                      </Typography>
                      <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={daData.commentFilesUrls} />
                    </Grid>
                  </Grid>
                )}

                {/* Section pour afficher les fichiers des commentaires internes */}
                {daData && daData.internalCommentFilesUrls && (
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        📎 Fichiers des commentaires internes
                      </Typography>
                      <FilesOpener onFileClick={handleCarouselOpen} FilesUrls={daData.internalCommentFilesUrls} />
                    </Grid>
                  </Grid>
                )}

                {/* Bouton de soumission */}
                <Grid container spacing={2} mt={2} justifyContent={'right'}>
                  <Grid item xs={5} sx={{ textAlign: 'right' }}>
                    <Button
                      disabled={!isFormCompletelyValid || isLoading || isSubmitting || hasSubmissionError}
                      onClick={handleConfirmClick}
                      variant="contained"
                      className="dealActionButton"
                      sx={{
                        marginTop: 1,
                      }}
                    >
                      {(isLoading || isSubmitting) && <CircularProgress sx={{ marginRight: 2 }} size={24} />}
                      {isSubmitting ? 'Soumission en cours...' : 'Soumettre la demande'}
                    </Button>
                    {!isFormCompletelyValid && (
                      <Box sx={{ textAlign: 'right', mt: 1 }}>
                        {!isValid && (
                          <Typography color="error" variant="body2">
                            Veuillez remplir tous les champs obligatoires
                          </Typography>
                        )}

                        {!requiredDocsValid && (
                          <Typography color="error" variant="body2">
                            {(() => {
                              const missingDocs = [];
                              if (certificateUrls.length === 0) missingDocs.push("Certificat d'immatriculation");
                              if (cerfaAchatUrls.length === 0) missingDocs.push("Cerfa de déclaration d'achat");
                              if (cerfaCessionUrls.length === 0) missingDocs.push('Cerfa de déclaration de cession');
                              if (mandatUrls.length === 0) missingDocs.push('Mandat à un professionnel');
                              return `Documents manquants : ${missingDocs.join(', ')}`;
                            })()}
                          </Typography>
                        )}
                        {!kbisValid && (
                          <Typography color="error" variant="body2">
                            Le KBIS est obligatoire pour "Autre entité"
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} maxWidth="md">
        <DialogContent>
          <Typography variant="h5" gutterBottom mb={6}>
            ✅ Confirmation de la déclaration d'achat
          </Typography>
          <Typography variant="body2">
            🤔 Êtes-vous sûr que toutes les informations sont correctes et que votre{' '}
            <strong>dossier est complet</strong> ?
            <span style={{ fontStyle: 'italic', display: 'block', marginTop: '10px' }}>
              ⚠️ Merci de bien vérifier les informations saisies car toute modification pourra entrainer des frais.
            </span>
          </Typography>
          <Divider sx={{ marginTop: 6, marginBottom: 6 }} />
          <Typography variant="body2">
            ⏱️ Afin de pouvoir traiter vos dossier au plus vite,{' '}
            <strong>
              📋 veuillez prendre connaisssance de la liste des pièces obligatoires demandées par l'ANTS pour pouvoir
              émettre des déclarations de cession et Changement de titulaire :
            </strong>
          </Typography>
          <Typography variant="body2">
            <ul>
              <li>🚗 Carte grise*</li>
              <li>📄 Mandat*</li>
              <li>📝 Déclaration d'achat papier*</li>
              <li>📋 Déclaration de cession papier*</li>
              <li>🧾 Ancien récépissé de déclaration d'achat</li>
            </ul>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button className="secondaryDealActionButton" onClick={() => setIsConfirmModalOpen(false)}>
            Annuler
          </Button>
          <Button
            className="dealActionButton"
            onClick={handleFinalConfirm}
            variant="contained"
            color="primary"
            disabled={isLoading || isSubmitting || hasSubmissionError}
          >
            {(isLoading || isSubmitting) && <CircularProgress sx={{ marginRight: 2 }} size={24} />}
            {isSubmitting ? 'Soumission en cours...' : isLoading ? 'Traitement en cours...' : 'Confirmer'}
          </Button>
        </DialogActions>
        {(isLoading || isSubmitting) && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" align="center" mt={1}>
              {currentStep}...
            </Typography>
            <Typography variant="body2" align="center" mt={1} color="textSecondary">
              Le processus peut parfois être long, merci de patienter.
            </Typography>
          </Box>
        )}
      </Dialog>

      <ErrorMessageSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        handleClose={() => setSnackbarOpen(false)}
        severity={severity}
      />

      <FileCarouselDialog
        allFiles={allCarouselFiles}
        initialIndex={carouselIndex}
        open={carouselOpen}
        onClose={() => setCarouselOpen(false)}
      />
    </Container>
  );
};

export default DAForm;
